import { useEffect, useState, useContext, useCallback } from "react";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Check, Sparkles, Gift, Copy, Crown } from "lucide-react";
import { EnlightenmentContext } from "@/context/EnlightenmentContext";
import { AuthContext } from "@/context/AuthContext";
import { fadeInUp, tapHover } from "@/lib/motionVariants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Subscription = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { sessionId, syncSession } = useContext(EnlightenmentContext);
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [plan, setPlan] = useState('monthly'); // 'monthly' | 'lifetime'
  const [referralCode, setReferralCode] = useState('');
  const [referralStatus, setReferralStatus] = useState({ has_discount: false, referred_by: null, lifetime_price: 101.08 });
  const [referralMsg, setReferralMsg] = useState(null);
  const [copyMsg, setCopyMsg] = useState('');

  const refreshReferral = useCallback(async () => {
    if (!sessionId) return;
    try {
      const res = await fetch(`${API}/referral/status/${sessionId}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setReferralStatus(data);
      }
    } catch (e) { console.warn('[Subscription] referral status fetch failed:', e?.message || e); }
  }, [sessionId]);

  useEffect(() => { refreshReferral(); }, [refreshReferral]);

  // Poll for payment status if returning from Stripe
  useEffect(() => {
    const checkoutSessionId = searchParams.get('session_id');
    const paymentType = searchParams.get('type');
    if (checkoutSessionId) {
      setPolling(true);
      let attempts = 0;
      const poll = async () => {
        if (attempts >= 8) {
          setPolling(false);
          setPaymentResult({ status: 'timeout' });
          return;
        }
        try {
          const res = await fetch(`${API}/subscription/status/${checkoutSessionId}`);
          const data = await res.json();
          if (data.payment_status === 'paid') {
            setPolling(false);
            setPaymentResult({ status: 'success', type: data.type || paymentType || 'monthly' });
            syncSession();
            return;
          }
          if (data.status === 'expired') {
            setPolling(false);
            setPaymentResult({ status: 'expired' });
            return;
          }
        } catch (err) { console.warn('[Subscription] poll failed:', err?.message || err); }
        attempts++;
        setTimeout(poll, 2000);
      };
      poll();
    }
  }, [searchParams, syncSession]);

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const endpoint = plan === 'lifetime' ? `${API}/subscribe/lifetime` : `${API}/subscribe`;
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId, origin_url: originUrl })
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error('[Subscription] checkout failed:', err?.message || err);
      alert('Could not open Stripe checkout — please refresh and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyReferral = async () => {
    setReferralMsg(null);
    const code = referralCode.trim().toLowerCase();
    if (!code) return;
    try {
      const res = await fetch(`${API}/referral/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, referral_code: code })
      });
      const data = await res.json();
      if (data.ok) {
        setReferralMsg({ type: 'success', text: data.message });
        setReferralCode('');
        syncSession();
        refreshReferral();
      } else {
        setReferralMsg({ type: 'error', text: data.error || 'Could not apply referral' });
      }
    } catch (err) {
      setReferralMsg({ type: 'error', text: 'Network error — try again' });
    }
  };

  const handleCopyCode = () => {
    if (!user?.email) return;
    navigator.clipboard.writeText(user.email).then(() => {
      setCopyMsg('Copied!');
      setTimeout(() => setCopyMsg(''), 1500);
    }).catch(() => {
      setCopyMsg('Press Ctrl+C');
      setTimeout(() => setCopyMsg(''), 1500);
    });
  };

  const lifetimePrice = referralStatus.lifetime_price;
  const lifetimeDiscounted = referralStatus.has_discount;
  const alreadyReferred = !!referralStatus.referred_by;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <motion.div
        {...fadeInUp}
        className="max-w-md mx-auto text-center w-full"
      >
        <motion.button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 mb-8 text-sm font-medium mx-auto"
          style={{ color: '#5C5470', fontFamily: "'Nunito', sans-serif" }}
          data-testid="back-from-subscription"
        >
          <ArrowLeft size={18} />
          Back to Primordeia
        </motion.button>

        {polling ? (
          <div className="text-center">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 1.5, ease: 'linear' }}
              className="w-12 h-12 mx-auto mb-4 rounded-full border-3 border-t-transparent"
              style={{ borderColor: '#4F378B', borderTopColor: 'transparent', borderWidth: 3 }}
            />
            <p className="text-lg font-semibold" style={{ color: '#4F378B', fontFamily: "'Fredoka', sans-serif" }}>
              Processing your payment...
            </p>
          </div>
        ) : paymentResult?.status === 'success' ? (
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="rounded-3xl p-8"
            style={{ backgroundColor: '#E8F5E8', boxShadow: '0 12px 40px rgba(76,175,80,0.15)' }}
            data-testid="payment-success"
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: '#4CAF50' }}>
              {paymentResult.type === 'lifetime' ? <Crown size={32} style={{ color: '#FFF' }} /> : <Check size={32} style={{ color: '#FFF' }} />}
            </div>
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Fredoka', sans-serif", color: '#2E7D32' }}>
              {paymentResult.type === 'lifetime' ? 'Lifetime Member of Primordeia' : 'Welcome Back to the Path'}
            </h2>
            <p className="text-sm mb-6" style={{ color: '#5C5470', fontFamily: "'Nunito', sans-serif" }}>
              {paymentResult.type === 'lifetime'
                ? 'Papaya is yours forever. The garden of paradise awaits.'
                : 'Your subscription is active. Papaya awaits you in Primordeia.'}
            </p>
            <button
              onClick={() => navigate('/journey')}
              className="px-8 py-3 rounded-full text-white font-semibold"
              style={{ background: 'linear-gradient(135deg, #4F378B 0%, #7B5EA7 100%)' }}
              data-testid="continue-journey-btn"
            >
              Continue the Journey
            </button>
          </motion.div>
        ) : (
          <motion.div
            className="rounded-3xl p-7"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(240,232,248,0.92) 100%)',
              backdropFilter: 'blur(12px)',
              boxShadow: '0 16px 48px rgba(79,55,139,0.15)'
            }}
            data-testid="subscription-card"
          >
            <div className="w-16 h-16 mx-auto mb-3 rounded-full overflow-hidden" style={{ boxShadow: '0 8px 24px rgba(79,55,139,0.2)' }}>
              <img src="https://customer-assets.emergentagent.com/job_scent-select-bunny/artifacts/kq5buvwj_3acdc1ca-655b-474d-9a20-97c4a3a5100a.png"
                alt="Papaya" className="w-full h-full object-cover" />
            </div>
            <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: "'Fredoka', sans-serif", color: '#4F378B' }}>
              Continue with Papaya
            </h2>
            <p className="text-sm mb-5" style={{ color: '#5C5470', fontFamily: "'Nunito', sans-serif" }}>
              Choose how you'd like to walk the path through Primordeia.
            </p>

            {/* Plan Toggle */}
            <div className="flex p-1 rounded-full mb-5" style={{ backgroundColor: '#F0E8F8' }}>
              <button
                onClick={() => setPlan('monthly')}
                className="flex-1 py-2 rounded-full text-sm font-semibold transition-all"
                style={{
                  background: plan === 'monthly' ? 'linear-gradient(135deg, #4F378B 0%, #7B5EA7 100%)' : 'transparent',
                  color: plan === 'monthly' ? '#FFF' : '#4F378B',
                  fontFamily: "'Fredoka', sans-serif"
                }}
                data-testid="plan-monthly-btn"
              >
                Monthly
              </button>
              <button
                onClick={() => setPlan('lifetime')}
                className="flex-1 py-2 rounded-full text-sm font-semibold transition-all flex items-center justify-center gap-1"
                style={{
                  background: plan === 'lifetime' ? 'linear-gradient(135deg, #4F378B 0%, #7B5EA7 100%)' : 'transparent',
                  color: plan === 'lifetime' ? '#FFF' : '#4F378B',
                  fontFamily: "'Fredoka', sans-serif"
                }}
                data-testid="plan-lifetime-btn"
              >
                <Crown size={14} /> Lifetime
              </button>
            </div>

            {/* Price card */}
            <div className="rounded-2xl p-5 mb-5" style={{ backgroundColor: '#F0E8F8' }} data-testid="price-card">
              {plan === 'monthly' ? (
                <>
                  <div className="text-3xl font-bold mb-1" style={{ color: '#4F378B', fontFamily: "'Fredoka', sans-serif" }}>
                    $5.79<span className="text-base font-normal" style={{ color: '#8B7DA8' }}>/month</span>
                  </div>
                  <div className="text-xs" style={{ color: '#8B7DA8' }}>Unlimited access to Papaya & all tiers</div>
                  <div className="text-[11px] mt-2 font-semibold" style={{ color: '#2E7D32' }} data-testid="cancel-anytime-note">
                    &#x2713; Cancel anytime from your profile — no lock-in
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-baseline justify-center gap-2 mb-1">
                    {lifetimeDiscounted && (
                      <span className="text-lg font-medium line-through" style={{ color: '#B8A8C8' }}>$101.08</span>
                    )}
                    <span className="text-3xl font-bold" style={{ color: '#4F378B', fontFamily: "'Fredoka', sans-serif" }} data-testid="lifetime-price">
                      ${lifetimePrice.toFixed(2)}
                    </span>
                  </div>
                  <div className="text-xs" style={{ color: '#8B7DA8' }}>
                    {lifetimeDiscounted
                      ? 'Lifetime — your referral discount is applied!'
                      : 'One-time payment — Papaya forever'}
                  </div>
                </>
              )}
            </div>

            <ul className="text-left space-y-2 mb-5">
              {(plan === 'lifetime'
                ? ['Lifetime access to Papaya', 'All tiers of enlightenment', 'Daily Oracle messages', 'Dream interpretation', 'Voice conversations', 'No recurring charges, ever']
                : ['Unlimited conversations with Papaya', 'All tiers of enlightenment', 'Daily Oracle messages', 'Dream interpretation', 'Voice conversations']
              ).map((feature) => (
                <li key={feature} className="flex items-center gap-2 text-sm" style={{ color: '#5C5470', fontFamily: "'Nunito', sans-serif" }}>
                  <Check size={16} style={{ color: '#4CAF50' }} />
                  {feature}
                </li>
              ))}
            </ul>

            <motion.button
              {...tapHover}
              onClick={handleSubscribe}
              disabled={loading}
              className="w-full py-4 rounded-full text-white font-bold text-base flex items-center justify-center gap-2"
              style={{
                background: 'linear-gradient(135deg, #4F378B 0%, #7B5EA7 100%)',
                boxShadow: '0 8px 24px rgba(79,55,139,0.3)',
                opacity: loading ? 0.7 : 1
              }}
              data-testid="subscribe-button"
            >
              {plan === 'lifetime' ? <Crown size={20} /> : <Sparkles size={20} />}
              {loading ? 'Redirecting...' : plan === 'lifetime' ? `Buy Lifetime — $${lifetimePrice.toFixed(2)}` : 'Subscribe Monthly'}
            </motion.button>

            {/* Referral section */}
            <div className="mt-6 pt-5 border-t" style={{ borderColor: 'rgba(79,55,139,0.15)' }}>
              <div className="flex items-center justify-center gap-2 mb-3">
                <Gift size={16} style={{ color: '#4F378B' }} />
                <h3 className="text-sm font-bold" style={{ color: '#4F378B', fontFamily: "'Fredoka', sans-serif" }}>
                  Referral Magic
                </h3>
              </div>

              {/* Apply a referral code */}
              {!alreadyReferred ? (
                <div className="mb-4">
                  <p className="text-xs mb-2" style={{ color: '#5C5470', fontFamily: "'Nunito', sans-serif" }}>
                    Got a friend's code? Enter it for <strong>30 extra free days</strong>.
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={referralCode}
                      onChange={(e) => setReferralCode(e.target.value)}
                      placeholder="friend@email.com"
                      className="flex-1 px-3 py-2 rounded-full text-sm border focus:outline-none"
                      style={{ borderColor: '#D5C5E8', backgroundColor: '#FFF', fontFamily: "'Nunito', sans-serif" }}
                      data-testid="referral-input"
                    />
                    <button
                      onClick={handleApplyReferral}
                      disabled={!referralCode.trim()}
                      className="px-4 py-2 rounded-full text-sm font-semibold text-white disabled:opacity-50"
                      style={{ background: 'linear-gradient(135deg, #4F378B 0%, #7B5EA7 100%)' }}
                      data-testid="referral-apply-btn"
                    >
                      Apply
                    </button>
                  </div>
                  {referralMsg && (
                    <p
                      className="text-xs mt-2"
                      style={{ color: referralMsg.type === 'success' ? '#2E7D32' : '#C62828', fontFamily: "'Nunito', sans-serif" }}
                      data-testid="referral-message"
                    >
                      {referralMsg.text}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-xs mb-4" style={{ color: '#2E7D32', fontFamily: "'Nunito', sans-serif" }} data-testid="referral-applied">
                  ✓ Referral applied — you got 30 extra days from {referralStatus.referred_by}.
                </p>
              )}

              {/* Share your code */}
              {user?.email ? (
                <div className="rounded-2xl p-3" style={{ backgroundColor: 'rgba(79,55,139,0.06)' }}>
                  <p className="text-xs mb-2" style={{ color: '#5C5470', fontFamily: "'Nunito', sans-serif" }}>
                    Share <strong>your code</strong>. Each friend who uses it earns themselves 30 extra days, and you earn the lifetime discount ($75.08).
                  </p>
                  <div className="flex items-center gap-2">
                    <code
                      className="flex-1 px-3 py-2 rounded-full text-xs truncate"
                      style={{ backgroundColor: '#FFF', color: '#4F378B', fontFamily: "'Nunito', sans-serif" }}
                      data-testid="user-referral-code"
                    >
                      {user.email}
                    </code>
                    <button
                      onClick={handleCopyCode}
                      className="px-3 py-2 rounded-full text-xs font-semibold flex items-center gap-1"
                      style={{ background: 'rgba(79,55,139,0.1)', color: '#4F378B' }}
                      data-testid="copy-referral-btn"
                    >
                      <Copy size={12} /> {copyMsg || 'Copy'}
                    </button>
                  </div>
                  {lifetimeDiscounted && (
                    <p className="text-xs mt-2" style={{ color: '#2E7D32', fontFamily: "'Nunito', sans-serif" }}>
                      ✨ Your friend used your code — lifetime is now $75.08!
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-xs" style={{ color: '#8B7DA8', fontFamily: "'Nunito', sans-serif" }}>
                  <button onClick={() => navigate('/auth')} className="underline" data-testid="signin-for-referral">Sign in</button> to get your own referral code.
                </p>
              )}
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default Subscription;
